import type { ApiClient, TokenGrant } from './client'
import type { Organization, StaffUser } from './types'

/**
 * Getting a staff principal, and giving one up.
 *
 * Every call here is `auth: false`. That is not a detail: the whole point of
 * these five endpoints is that they work without a token, and marking one as
 * authenticated would make it ask the client for a token it cannot have yet.
 * The refresh endpoint in particular is called from inside the client's own
 * token path, so an authenticated refresh would recurse.
 *
 * `login` returns the caller's user and organization alongside the tokens
 * rather than leaving the console to fetch them next. It saves a round trip on
 * the slowest screen in the product, and more importantly it means there is no
 * window in which the app is holding a token and does not yet know whose it is:
 * a route guard reading a half-populated session is how a viewer gets a flash
 * of a screen they may not have.
 *
 * `POST /v1/org/auth/logout` is deliberately not in this module. Revoking a
 * refresh token means naming it, and the only thing that holds one is the
 * client, so `ApiClient.signOut()` owns that call. A method here would have to
 * be handed the token by somebody, and the only way to do that is to let the
 * token back out of the closure that exists to keep it in.
 */

export interface LoginInput {
  email: string
  password: string
}

/** A grant plus who it belongs to. The console needs both to render anything. */
export interface StaffSession extends TokenGrant {
  user: StaffUser
  organization: Organization
}

export interface AcceptInviteInput {
  /** From the emailed link. Single use, and short-lived. */
  token: string
  /** The console's own vocabulary for a staff member's name. See `acceptInvite`. */
  name: string
  password: string
}

export interface ResetPasswordInput {
  token: string
  password: string
}

export function authApi(http: ApiClient) {
  return {
    login(input: LoginInput) {
      return http.request<StaffSession>('/v1/org/auth/login', {
        method: 'POST',
        auth: false,
        body: input,
      })
    },

    /**
     * Called by the client's own refresh path, not by screens.
     *
     * Exposed here so the endpoint is written down with the rest of the API
     * surface, and because the invite and reset flows end in a grant that has
     * to be adopted the same way.
     */
    refresh(refreshToken: string) {
      return http.request<TokenGrant>('/v1/org/auth/refresh', {
        method: 'POST',
        auth: false,
        body: { refreshToken },
      })
    },

    acceptInvite(input: AcceptInviteInput) {
      /*
       * Translated rather than forwarded as `body: input`, and the reason is a
       * bug that shipped from skipping this exact step. `StaffUser.name` is
       * this console's own vocabulary for a signed-in member of staff, chosen
       * to read plainly on the screens that show one; the server's route
       * validates a field called `displayName`, chosen to match the column it
       * lands in. Forwarding the input object verbatim sent a `name` key the
       * server's validator does not know, which it silently drops, so the
       * required `displayName` arrived as nothing at all and the request was
       * refused with "Invalid displayName." Every other method in this file
       * can forward its input directly because its fields already agree with
       * the wire; this is the one place they do not, and now the one place
       * that says so.
       */
      return http.request<StaffSession>('/v1/org/auth/invite/accept', {
        method: 'POST',
        auth: false,
        body: { token: input.token, displayName: input.name, password: input.password },
      })
    },

    /**
     * Always resolves, whether or not the address belongs to anybody.
     *
     * The server answers the same way for a known and an unknown address, so
     * this endpoint cannot be used to find out who has an account here. The
     * console shows the same confirmation for the same reason: a screen that
     * says "no such user" undoes the server's discretion.
     */
    forgotPassword(email: string) {
      return http.request<void>('/v1/org/auth/password/forgot', {
        method: 'POST',
        auth: false,
        body: { email },
      })
    },

    resetPassword(input: ResetPasswordInput) {
      return http.request<StaffSession>('/v1/org/auth/password/reset', {
        method: 'POST',
        auth: false,
        body: input,
      })
    },
  }
}

export type AuthApi = ReturnType<typeof authApi>
